import React, { useState, useEffect, useMemo } from "react";
import { Plus, Inbox, ChevronRight, X, Trash2 } from "lucide-react";
import { C } from "../igualdad-data.js";
import { Status } from "../igualdad-components.jsx";
import { apiGetAll, apiPost, apiPut, apiDelete } from "../igualdad-api.js";
import { useIgualdad } from "../igualdad-store.jsx";

/* ============================================================
   Talention — Módulo Igualdad · Tickets
   ============================================================ */

const COLS = [
  { key: "pendiente", label: "Pendiente" },
  { key: "en_curso", label: "En curso" },
  { key: "en_revision", label: "En revisión" },
  { key: "resuelto", label: "Resuelto" },
];
const TICKET_TONE = { alta: C.danger, media: C.brand, baja: C.success };

function IgualdadTickets() {
  const { session, fireToast } = useIgualdad();
  const politicaId = session.politicaId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tickets, setTickets] = useState([]);

  const [modal, setModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formErr, setFormErr] = useState(null);
  const [form, setForm] = useState({ titulo: "", prioridad: "media" });
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setTickets(await apiGetAll("/tickets", {}));
    } catch (e) {
      setError(e.message || true);
    } finally {
      setLoading(false);
    }
  }

  // El origen recarga al cambiar de política aunque la petición no la filtre.
  useEffect(() => { load(); }, [politicaId]);

  const byCol = useMemo(() => {
    const m = {};
    COLS.forEach((c) => (m[c.key] = []));
    tickets.forEach((t) => { (m[t.estado] ||= []).push(t); });
    return m;
  }, [tickets]);

  function nextEstado(estado) {
    const i = COLS.findIndex((c) => c.key === estado);
    return i >= 0 && i < COLS.length - 1 ? COLS[i + 1].key : null;
  }

  async function advance(t) {
    const next = nextEstado(t.estado);
    if (!next) return;
    try {
      await apiPut(`/tickets/${t.id}`, { estado: next });
      await load();
    } catch (e) { fireToast(e.message || "No se pudo mover la tarea."); }
  }

  async function remove() {
    if (deleting || !confirmDel) return;
    setDeleting(true);
    try {
      await apiDelete(`/tickets/${confirmDel.id}`);
      fireToast("Tarea eliminada.");
      setConfirmDel(null);
      await load();
    } catch (e) { fireToast(e.message || "No se pudo eliminar."); }
    finally { setDeleting(false); }
  }

  function openNew() { setForm({ titulo: "", prioridad: "media" }); setFormErr(null); setModal(true); }

  async function create() {
    if (saving) return;
    setSaving(true);
    setFormErr(null);
    try {
      await apiPost("/tickets", {
        titulo: form.titulo,
        prioridad: form.prioridad,
        estado: "pendiente",
        proyecto_id: session.politica?.proyecto_id ?? session.politica?.proyecto?.id ?? null,
      });
      setModal(false);
      fireToast("Tarea creada.");
      await load();
    } catch (e) {
      setFormErr(e.errors ? Object.values(e.errors).flat().join(" ") : (e.message || "No se pudo crear."));
    } finally { setSaving(false); }
  }

  return (
    <div className="rb-page">
      <div className="rb-pagehead"><div><h1 className="rb-h1">Tickets y tareas</h1><p className="rb-lead">Solicitudes de información, documentación pendiente y revisiones del proyecto, con trazabilidad.</p></div><button className="rb-btn-soft" onClick={openNew}><Plus size={14} /> Nueva tarea</button></div>

      {(loading || error) ? (
        <Status loading={loading} error={error} />
      ) : (
        <div className="rb-kanban">
          {COLS.map((col) => (
            <div key={col.key} className="rb-kancol">
              <div className="rb-kanh"><Inbox size={14} /> {col.label}<span className="rb-kancount">{byCol[col.key].length}</span></div>
              {byCol[col.key].map((tk) => (
                <div key={tk.id} className="rb-kancard">
                  <div className="rb-kancardtop">
                    <div className="rb-kantitle">{tk.titulo}</div>
                    <button className="rb-kandel" title="Eliminar" onClick={() => setConfirmDel(tk)}><Trash2 size={13} /></button>
                  </div>
                  <div className="rb-kanmeta"><span className="rb-soft">{tk.cliente?.nombre || tk.proyecto?.nombre || "—"}</span></div>
                  <div className="rb-kanfoot">
                    <span className="rb-kanresp">{tk.responsable_rol || tk.asignado?.name || "Sin asignar"}</span>
                    <span className="rb-kanpri" style={{ background: (TICKET_TONE[tk.prioridad] || C.textMuted) + "22", color: TICKET_TONE[tk.prioridad] || C.textMuted }}>{tk.prioridad_label || tk.prioridad}</span>
                  </div>
                  {nextEstado(tk.estado) ? (
                    <button className="rb-kanmove" onClick={() => advance(tk)}>Mover a «{COLS.find((c) => c.key === nextEstado(tk.estado)).label}» <ChevronRight size={12} /></button>
                  ) : null}
                </div>
              ))}
              {!byCol[col.key].length ? <div className="rb-kanempty">—</div> : null}
            </div>
          ))}
        </div>
      )}

      {modal ? (
        <div className="rb-modal-bg" onClick={() => setModal(false)}>
          <div className="rb-modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Nueva tarea</div><div className="rb-modal-s">Se crea en estado «Pendiente»</div></div>
              <button className="rb-ico" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b">
              <label>Título de la tarea</label>
              <input className="rb-inp rb-inp-gap" value={form.titulo} onChange={(ev) => setForm({ ...form, titulo: ev.target.value })} placeholder="p. ej. Solicitar tablas salariales del convenio" />
              <label>Prioridad</label>
              <select value={form.prioridad} onChange={(ev) => setForm({ ...form, prioridad: ev.target.value })}><option value="alta">Alta</option><option value="media">Media</option><option value="baja">Baja</option></select>
              {formErr ? <div className="rb-formerr">{formErr}</div> : null}
            </div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setModal(false)}>Cancelar</button>
              <button className="rb-btn" disabled={saving} onClick={create}>{saving ? "Guardando…" : "Crear tarea"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDel ? (
        <div className="rb-modal-bg" onClick={() => setConfirmDel(null)}>
          <div className="rb-modal rb-modal-sm" onClick={(ev) => ev.stopPropagation()}>
            <div className="rb-modal-h">
              <div><div className="rb-modal-t">Eliminar tarea</div><div className="rb-modal-s">Esta acción no se puede deshacer.</div></div>
              <button className="rb-ico" onClick={() => setConfirmDel(null)}><X size={18} /></button>
            </div>
            <div className="rb-modal-b"><p className="rb-soft">¿Seguro que quieres eliminar <b>{confirmDel.titulo}</b>?</p></div>
            <div className="rb-modal-f">
              <button className="rb-btn-ghost" onClick={() => setConfirmDel(null)}>Cancelar</button>
              <button className="rb-btn rb-btn-danger" disabled={deleting} onClick={remove}>{deleting ? "Eliminando…" : "Eliminar"}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export { IgualdadTickets };
